import { useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/auth';
import { useToast } from '@/shared/ui/toast';
import type { OrgRole } from '@/entities/organization';

interface OrgRoleGuardProps {
  /**
   * Org-level roles allowed to access the protected children.
   * A session whose `orgRole` is null (no org membership) is always denied.
   */
  roles: OrgRole[];
  children: ReactNode;
}

export const OrgRoleGuard = ({ children, roles }: OrgRoleGuardProps) => {
  const { sessionUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const userOrgRole = sessionUser?.orgRole ?? null;
  const allowed = userOrgRole !== null && roles.includes(userOrgRole as OrgRole);

  useEffect(() => {
    if (!allowed) {
      toast({ title: t('organization.errors.forbidden'), variant: 'destructive' });
    }
  }, [allowed, toast, t]);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
